import type { CardLimitsProvenance } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * Shared hero domain types, plus the demo values the hero falls back to.
 *
 * Card identity is always live — EX-40595 wired it to the credit card detail
 * endpoint and there is no demo fallback for it. Everything else here is served
 * when `show_live_data_card_header` is off, which is the default:
 *
 * - `limits` — the live path for these does work (`READ_PHYSICAL_CARD_LIMIT` is
 *   a real action), so this is a deliberate demo default rather than a stand-in
 *   for something missing.
 * - `billing` and `credentials` — PAN/CVC have no contract counterpart at all
 *   and the billing-address endpoint is not built, so until EX-40580 lands this
 *   is the only path that renders anything.
 *
 * One flag covers both, so the hero reads wholly from here or wholly from the
 * live client, never a mix of the two.
 *
 * The grouping is deliberate: each group corresponds to an *independently*
 * fetched endpoint, and regions read only the group they need, so a limits
 * failure cannot blank the identity row.
 *
 * Money is always minor units plus an ISO-4217 currency, never a float —
 * conversion to a display amount happens at the formatter call site.
 */

/** Contract enum. LOCKED is issuer-imposed and is NOT a voluntary FROZEN. */
export type PhysicalCardManagementStatus =
  | 'PENDING_ISSUANCE'
  | 'ACTIVE'
  | 'FROZEN'
  | 'LOCKED'
  | 'CANCELED'
  | 'FAILED'

export type Money = {
  amountMinor: number
  currency: string
}

export const CARD_DETAIL_HERO_DEMO = {
  /** Mirrors GET /physical-cards/{id}/limits — separate groups, never flattened. */
  limits: {
    permanentCardSpendControl: {
      limit: { amountMinor: 5000000, currency: 'USD' } as Money,
    },
    cardFinancials: {
      balance: { amountMinor: 770000, currency: 'USD' } as Money,
      availableSpend: { amountMinor: 4230000, currency: 'USD' } as Money,
    },
    /** The demo path never issues a limit change, so any version serves. */
    version: 1,
    /**
     * `CardLimitsProvenance` has no neutral member — the region renders a chip
     * for SURROGATE_PARTNER and nothing for LIVE_PARTNER — so this takes the
     * value that renders no chip and leaves the figures unannotated.
     */
    provenance: 'LIVE_PARTNER' as CardLimitsProvenance,
  },

  /** Mirrors GET /physical-cards/{id}/billing-address — scope CARD_ACCOUNT. */
  billing: {
    address: {
      line1: '120 Broadway',
      line2: 'Suite 400',
      locality: 'New York',
      region: 'NY',
      postalCode: '10001',
      countryCode: 'US',
    },
  },

  /**
   * Reveal values for the demo path, so the disclosure can be exercised before
   * EX-40580 provides a live source.
   *
   * The card's expiry is NOT here: it is a real field on the credit card detail
   * response and is carried on the `card` group instead.
   */
  credentials: {
    /**
     * Leading groups only — the trailing group is the card's REAL last four,
     * joined at render (see `CardDetailHero`). Two reasons it is split:
     *
     * 1. No card-number-shaped literal then exists in the source or the shipped
     *    bundle. Secret scanners match on shape and issuer prefix, and a demo
     *    constant is not worth the conversation it would otherwise start.
     * 2. It is truer. The masked card face already shows the real last four, so
     *    a whole fake PAN made revealing *change* the last four — the one part
     *    of the number the user could already see. Now revealing only fills in
     *    the leading digits, which is what a real reveal does.
     */
    panLeadingGroups: '0000 0000 0000',
    cvc: '567',
  },
} as const
